/**
 * Credential-safe error logging.
 *
 * An axios error carries the whole request: `config.headers` (the session
 * `Cookie`, the `Authorization` bearer, the 60-second `X-Files-Grant`),
 * `config.auth`, `config.data` and the live `request` socket. Handing one
 * straight to `console.error` / `logger.error` writes all of that to stdout,
 * and from there to Docker logs, Loki and anything that scrapes them. A
 * fetch/undici failure is safer on its own but its `cause` chain can still
 * name the URL it dialled, query string included.
 *
 * `toSafeErrorLog(err)` reduces any thrown value to a minimal, plain object:
 * what failed, where (URL without query string), and the upstream status plus a
 * truncated body — never headers, request config or credentials. Pure (no
 * server-only imports) so it is unit-testable and importable anywhere.
 */

/** Header names whose values are credentials, matched case-insensitively. */
const SENSITIVE_HEADER_NAMES = new Set([
  'cookie',
  'set-cookie',
  'authorization',
  'proxy-authorization',
  'x-api-key',
  'x-files-grant'
])

/**
 * Anything that looks like a credential by name: `token`, but not a token
 * count (`input_tokens`, `max_tokens`, `token_count`).
 */
const SENSITIVE_KEY_PATTERN =
  /token(?!s(?:$|[._])|_?count)|secret|grant|signature|password|passwd|credential|api[-_]?key/i

const REDACTED = '[REDACTED]'
const MAX_BODY_CHARS = 500
const MAX_STRING_CHARS = 2000
const MAX_DEPTH = 4
const MAX_CAUSE_DEPTH = 3

export function isSensitiveKey(key: string): boolean {
  return (
    SENSITIVE_HEADER_NAMES.has(key.toLowerCase()) ||
    SENSITIVE_KEY_PATTERN.test(key)
  )
}

const BEARER_PATTERN = /\b(Bearer|Basic)\s+[A-Za-z0-9._~+/=-]+/gi

/** `Cookie: abc`, `"authorization":"abc"`, `x-files-grant=abc` in free text. */
const HEADER_IN_TEXT_PATTERN =
  /\b(cookie|set-cookie|authorization|proxy-authorization|x-api-key|x-files-grant)\b(["']?\s*[:=]\s*["']?)[^\n"',}]+/gi

/**
 * `?token=abc` / `&sig=abc`, and a form body or bare query that STARTS with
 * one (`client_secret=abc&...`, Sentry's `http.query`): the name carries the
 * sensitivity, the value is dropped.
 */
const SENSITIVE_PARAM_PATTERN =
  /((?:^|[?&;\s])[^=&#\s"']*(?:token|secret|grant|signature|sig|key|password|passwd|credential|auth|session)[^=&#\s"']*=)[^&#\s"']*/gim

/** `"access_token": "abc"` in a JSON text (a body echoed into a message or string body). */
const SENSITIVE_JSON_PATTERN =
  /("[^"\n]*(?:token|secret|grant|signature|password|passwd|credential|api[-_]?key|cookie|authorization)[^"\n]*"\s*:\s*)"(?:[^"\\\n]|\\.)*"/gi

/** A JWT (three base64url segments, header starting `eyJ`) wherever it appears. */
const JWT_PATTERN = /\beyJ[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]*/g

/** Absolute URL followed by a query string or fragment. */
const URL_QUERY_PATTERN =
  /(\b[a-z][a-z0-9+.-]*:\/\/[^\s?#"'<>]+)[?#][^\s"'<>]*/gi

/** `user:pass@` in an absolute URL. */
const URL_USERINFO_PATTERN = /(\b[a-z][a-z0-9+.-]*:\/\/)[^\s/@"'<>]+@/gi

export interface ScrubStringOptions {
  /** Drop the whole query string from absolute URLs, not just sensitive params. */
  stripQueries?: boolean
}

/** Redacts credentials embedded in free text (messages, stacks, bodies, URLs). */
export function scrubSensitiveString(
  value: string,
  options: ScrubStringOptions = {}
): string {
  let out = value
    .replace(URL_USERINFO_PATTERN, `$1${REDACTED}@`)
    .replace(BEARER_PATTERN, `$1 ${REDACTED}`)
    .replace(HEADER_IN_TEXT_PATTERN, `$1$2${REDACTED}`)
    .replace(SENSITIVE_JSON_PATTERN, `$1"${REDACTED}"`)
    .replace(JWT_PATTERN, REDACTED)
  if (options.stripQueries) out = out.replace(URL_QUERY_PATTERN, '$1')
  return out.replace(SENSITIVE_PARAM_PATTERN, `$1${REDACTED}`)
}

function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max)}…[truncated]` : value
}

/**
 * Deep-copies `value` with sensitive keys redacted and strings scrubbed.
 * Depth-limited and cycle-safe; used for response bodies and Sentry payloads.
 */
export function redactSensitive(
  value: unknown,
  depth = 0,
  seen: WeakSet<object> = new WeakSet()
): unknown {
  if (typeof value === 'string') {
    return truncate(scrubSensitiveString(value), MAX_STRING_CHARS)
  }
  if (value === null || typeof value !== 'object') return value
  if (depth >= MAX_DEPTH) return '[Object]'
  if (seen.has(value)) return '[Circular]'
  seen.add(value)
  if (Array.isArray(value)) {
    return value.map((item) => redactSensitive(item, depth + 1, seen))
  }
  const out: Record<string, unknown> = {}
  for (const [key, item] of Object.entries(value)) {
    out[key] = isSensitiveKey(key)
      ? REDACTED
      : redactSensitive(item, depth + 1, seen)
  }
  return out
}

/** URL without userinfo, query string or fragment. Never throws. */
function stripUrlSecrets(url: string): string {
  try {
    const parsed = new URL(url)
    return `${parsed.origin}${parsed.pathname}`
  } catch {
    return scrubSensitiveString(url.split(/[?#]/)[0] ?? '', {
      stripQueries: true
    })
  }
}

function describeBody(data: unknown): unknown {
  if (data === undefined || data === null) return undefined
  if (typeof data === 'string') {
    return truncate(scrubSensitiveString(data), MAX_BODY_CHARS)
  }
  if (typeof data !== 'object') return data
  // Streams / buffers (responseType: 'stream' | 'arraybuffer') are not logged.
  if (typeof (data as { pipe?: unknown }).pipe === 'function') return '[stream]'
  if (ArrayBuffer.isView(data) || data instanceof ArrayBuffer) return '[binary]'
  try {
    return truncate(JSON.stringify(redactSensitive(data)) ?? '', MAX_BODY_CHARS)
  } catch {
    return '[unserializable]'
  }
}

interface AxiosLikeError {
  isAxiosError?: boolean
  code?: unknown
  status?: unknown
  config?: {
    method?: unknown
    url?: unknown
    baseURL?: unknown
  }
  response?: {
    status?: unknown
    statusText?: unknown
    data?: unknown
  }
  cause?: unknown
}

export interface SafeErrorLog {
  name?: string
  message: string
  code?: string | number
  status?: number
  method?: string
  url?: string
  response?: { status?: number; statusText?: string; body?: unknown }
  stack?: string
  cause?: SafeErrorLog
}

const asString = (v: unknown): string | undefined =>
  typeof v === 'string' && v ? v : undefined
const asNumber = (v: unknown): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) ? v : undefined

function requestUrl(config: AxiosLikeError['config']): string | undefined {
  const url = asString(config?.url)
  const baseURL = asString(config?.baseURL)
  if (!url && !baseURL) return undefined
  if (url && /^[a-z][a-z0-9+.-]*:\/\//i.test(url)) return stripUrlSecrets(url)
  const joined = `${(baseURL ?? '').replace(/\/+$/, '')}/${(url ?? '').replace(/^\/+/, '')}`
  return stripUrlSecrets(joined.endsWith('/') && !url ? baseURL! : joined)
}

function summarize(err: unknown, depth: number): SafeErrorLog {
  if (typeof err === 'string') {
    return { message: truncate(scrubSensitiveString(err), MAX_STRING_CHARS) }
  }
  if (err === null || typeof err !== 'object') {
    return { message: String(err) }
  }

  const e = err as Error & AxiosLikeError & Record<string, unknown>
  const out: SafeErrorLog = {
    message: truncate(
      scrubSensitiveString(
        typeof e.message === 'string' ? e.message : 'Non-Error value thrown',
        { stripQueries: true }
      ),
      MAX_STRING_CHARS
    )
  }
  if (asString(e.name)) out.name = e.name
  const code = asString(e.code) ?? asNumber(e.code)
  if (code !== undefined) out.code = code
  const status = asNumber(e.status) ?? asNumber(e.response?.status)
  if (status !== undefined) out.status = status
  const method = asString(e.config?.method)
  if (method) out.method = method.toUpperCase()
  const url = requestUrl(e.config)
  if (url) out.url = url
  if (e.response) {
    const body = describeBody(e.response.data)
    out.response = {
      status: asNumber(e.response.status),
      statusText: asString(e.response.statusText),
      ...(body !== undefined ? { body } : {})
    }
  }
  if (typeof e.stack === 'string') {
    out.stack = scrubSensitiveString(e.stack, { stripQueries: true })
  }
  if (e.cause !== undefined && depth < MAX_CAUSE_DEPTH) {
    out.cause = summarize(e.cause, depth + 1)
  }
  return out
}

/**
 * Reduces a thrown value (axios error, fetch/undici error, plain Error, string)
 * to a loggable object that cannot carry request headers or credentials.
 */
export function toSafeErrorLog(err: unknown): SafeErrorLog {
  return summarize(err, 0)
}

/** Makes an own property non-enumerable: still readable, never serialized. */
function hideProperty(target: object, key: string): void {
  const descriptor = Object.getOwnPropertyDescriptor(target, key)
  if (descriptor?.enumerable && descriptor.configurable !== false) {
    Object.defineProperty(target, key, { ...descriptor, enumerable: false })
  }
}

/**
 * Takes the request internals of an axios error out of everything that
 * serializes it, without changing what code can read from it.
 *
 * `config` (headers, `auth`, the OAuth `client_secret` form body), `request`
 * (whose `_header` is the raw request text, `Authorization:` line included)
 * and `response.config` / `response.request` / `response.headers` (an
 * upstream `Set-Cookie` session) become non-enumerable, so `console.error(err)`
 * (util.inspect), pino's `err` serializer, Sentry's normalizer and
 * `JSON.stringify` all skip them, while `err.config.url` or
 * `err.response.headers['retry-after']` keep working. `toJSON` is replaced
 * with `toSafeErrorLog`. A safety net under `toSafeErrorLog` for the raw
 * `console.error('...', error)` call sites that remain.
 */
export function concealRequestInternals<E>(error: E): E {
  if (error === null || typeof error !== 'object') return error
  hideProperty(error, 'config')
  hideProperty(error, 'request')
  const response = (error as { response?: unknown }).response
  if (response !== null && typeof response === 'object') {
    hideProperty(response, 'config')
    hideProperty(response, 'request')
    hideProperty(response, 'headers')
  }
  Object.defineProperty(error, 'toJSON', {
    value: () => toSafeErrorLog(error),
    enumerable: false,
    configurable: true,
    writable: true
  })
  return error
}

interface InterceptableClient {
  interceptors: {
    response: {
      use: (
        onFulfilled: undefined,
        onRejected: (error: unknown) => unknown
      ) => unknown
    }
  }
}

/**
 * Registers `concealRequestInternals` on every rejection from an axios
 * instance that sends credentials. Typed structurally so this module stays
 * free of an axios import.
 */
export function concealRequestInternalsOn<C extends InterceptableClient>(
  client: C
): C {
  client.interceptors.response.use(undefined, (error) =>
    Promise.reject(concealRequestInternals(error))
  )
  return client
}
